/**
 * Thin facade over the generated FE→BFF clients for the
 * "create follow-up from calendar event" flow.
 *
 * Keeps the call-sites in `CreateFollowupFromEventDialog` decoupled from the
 * generated client surface: the dialog imports these two functions and the
 * `PartialObjectiveContext` shape, nothing else.
 *
 * Endpoints:
 * - `createServiceProviderFollowupFromEvent` → `ICitizenServiceProviderCalendarClient`
 * - `getServiceProviderPartialObjective`     → `ICitizenAssignmentClient`
 */

import { PartialObjectiveStatusType } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';

const calendarClient = moduleClientFactory.createCitizenServiceProviderCalendarClient(true);
const assignmentClient = moduleClientFactory.createCitizenAssignmentClient(true);

export interface CreateFollowupFromEventRequest {
  citizenId: string;
  eventId: string;
  progression: number; // 1 | 0 | -1
  description: string;
  methodType?: string; // Guid
}

export interface GetPartialObjectiveRequest {
  citizenId: string;
  partialObjectiveId: string;
}

export interface PartialObjectiveContext {
  title: string;
  description: string;
  status: PartialObjectiveStatusType;
  methodType?: string; // Guid
  interventionObjectiveTitle: string;
  interventionObjectiveDescription: string;
}

export async function createServiceProviderFollowupFromEvent(
  request: CreateFollowupFromEventRequest
): Promise<void> {
  await calendarClient.createServiceProviderFollowupFromEvent({
    citizenId: request.citizenId,
    eventId: request.eventId,
    progression: request.progression,
    description: request.description,
    methodType: request.methodType,
  });
}

export async function getServiceProviderPartialObjective(
  request: GetPartialObjectiveRequest
): Promise<PartialObjectiveContext> {
  const response = await assignmentClient.getServiceProviderPartialObjective({
    citizenId: request.citizenId,
    partialObjectiveId: request.partialObjectiveId,
  });

  return {
    title: response.title ?? '',
    description: response.description ?? '',
    status: response.status,
    methodType: response.methodType,
    interventionObjectiveTitle: response.interventionObjectiveTitle ?? '',
    interventionObjectiveDescription: response.interventionObjectiveDescription ?? '',
  };
}
